import React from 'react';
import { Page as RPDFPage, View, Text, StyleSheet } from '@react-pdf/renderer';
import { pdfColors, pdfFontFamily, pdfPageFrame } from '../theme';
import type { SchoolBranding } from '../types';
import OfficialHeader, { type OfficialDetail } from './OfficialHeader';
import Footer from './Footer';
import Watermark from './Watermark';

/** Body text size on official documents — the serif the letter is set in. */
export const OFFICIAL_BODY = 11.5;

interface Props {
  branding: SchoolBranding;
  size?: 'A4' | 'A5';
  /** "Fine Letter", "Fee Receipt" — set in italics under the school's name. */
  title: string;
  /** Who it concerns, in the letterhead. */
  details?: OfficialDetail[];
  /** The document's number, printed left under the letterhead — "Ref. FIN-2026-…". */
  reference: { label: string; value: string };
  /** The date, printed right — "1 November 2026". */
  date: string;
  watermark?: string;
  generatedAt: string;
  children: React.ReactNode;
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: pdfPageFrame.contentBottom,
    fontFamily: pdfFontFamily.sans,
    color: pdfColors.textPrimary,
    backgroundColor: pdfColors.surface,
  },
  refRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 22 },
  ref: { fontFamily: pdfFontFamily.mono, fontSize: 8.5, color: pdfColors.textSecondary },
  date: { fontFamily: pdfFontFamily.serif, fontSize: OFFICIAL_BODY, color: pdfColors.textPrimary },
});

/**
 * The page every document the school sends home is printed on — letters and
 * receipts: the official letterhead (OfficialHeader), the document's number
 * and date beneath it, and the footer saying it is valid when signed. Mark
 * it void with `watermark` ("CANCELLED", "REVERSED").
 */
const OfficialPage: React.FC<Props> = ({ branding, size = 'A4', title, details, reference, date, watermark, generatedAt, children }) => (
  <RPDFPage size={size} style={[styles.page, { paddingHorizontal: size === 'A4' ? 48 : 34 }]} wrap>
    {watermark && <Watermark text={watermark} compact={size === 'A5'} />}
    <OfficialHeader branding={branding} title={title} details={details} compact={size === 'A5'} />
    <View style={styles.refRow} wrap={false}>
      <Text style={styles.ref}>{reference.label} {reference.value}</Text>
      <Text style={styles.date}>{date}</Text>
    </View>
    {children}
    {/* Letters and receipts both carry lines to sign. */}
    <Footer meta={{ title, generatedAt, signed: true }} />
  </RPDFPage>
);

export default OfficialPage;
